import React, { useCallback, useEffect } from 'react';
import { animated, useSprings, config } from '@react-spring/web';
import { easeSinInOut } from 'd3-ease';
import { mergeClassNames } from '../../../../../utils/mergeClassnames';
import { useHover } from '../../../../../utils/useHover';
import styles from './index.module.scss';

export function Spots({ spots, setActive }) {
  spots = spots ? spots : [];
  return spots.map((spot, index) => <Spot key={index} spot={spot} onClick={setActive} queuePosition={index} />);
}

export default function Spot({ onClick, spot, queuePosition }) {
  let x = Number(spot?.position.x);
  let y = Number(spot?.position.y);

  let [hoverRef, isHover] = useHover();
  const setThisSpotActive = useCallback(() => onClick(spot), [onClick, spot]);

  return (
    <button
      style={{ left: `${x}%`, top: `${y}%` }}
      className={mergeClassNames(styles.spot, styles[spot?.type])}
      onClick={setThisSpotActive}
      ref={hoverRef}
    >
      <SpotElements isHover={isHover} queuePosition={queuePosition} />
    </button>
  );
}

const RESPIRATIONAL_PULSE_LENGTH = 1600;
const RESPIRATIONAL_PULSE_CONFIG = { duration: RESPIRATIONAL_PULSE_LENGTH, easing: easeSinInOut };
const START_DELAY = 700;

const circles = [
  {
    r: 25,
    className: styles.circle,
    startScale: 1,
    stopScale: 1.2,
    activeScale: 1.3,
    strokeWidth: 2,
  },
  {
    r: 14,
    className: styles.dot,
    startScale: 1,
    stopScale: 0.8,
    activeScale: 0.9,
  },
];

function SpotElements({ isHover, queuePosition }) {
  let [springsProps, animate] = useSprings(
    circles.length,
    circles.map(circle => ({ ...getDefaultSpring(circle), delay: START_DELAY * queuePosition, from: { r: 0 } })),
    [queuePosition]
  );

  useEffect(() => {
    animate.start(index =>
      isHover
        ? { to: { r: circles[index].r * circles[index].activeScale }, config: config.stiff }
        : {
            to: { r: circles[index].r * circles[index].startScale },
            onRest: () =>
              Boolean(index) && animate.start(index => ({ ...getDefaultSpring(circles[index]), reset: true })),
          }
    );
  }, [animate, isHover]);

  return (
    <animated.svg viewBox="0 0 100 100" className={styles.spotElement}>
      {springsProps.map((animProps, index) => {
        let { r, className, strokeWidth } = circles[index];
        return (
          <animated.circle
            key={index}
            style={animProps}
            className={className}
            cx="50"
            cy="50"
            r={r}
            strokeWidth={strokeWidth}
          />
        );
      })}
    </animated.svg>
  );
}

function getDefaultSpring({ startScale, stopScale, r }) {
  return {
    loop: { reverse: true },
    to: { r: r * stopScale },
    from: { r: r * startScale },
    config: RESPIRATIONAL_PULSE_CONFIG,
  };
}
